import { useState, useRef, useCallback, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import Webcam from "react-webcam";
import { useSelector, useDispatch } from "react-redux";
import {
  setInferenceTime,
  setIsLoading,
  setIsDetecting,
  setDetectionImage,
  setNumBoxes,
} from "../features/detectSlice";
import { setLogout } from "../features/authSlice";
import useCam from "../hooks/useCam";
import { color_map, processTfjsYOLOOutput } from "../yolo_tf";
import useLocation from "../hooks/useLocation";
import { useDetect } from "../contexts/DetectContext";
import SwitchButton from "./SwitchButton";
import MultiSelect from "./MultiSelect";
import style from "./VideoDetection.module.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";

const API_URL = process.env.REACT_APP_API_URL || "https://api.up2smart.com:5001";
const MIN_UPLOAD_INTERVAL = 5000; // Minimum 5 seconds between uploads

function VideoDetection({
  isCollecting = false,
  isDisplay = true,
  setImageCount,
  activeButton = "Container",
  setActiveButton,
}) {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);
  const isAdmin = user.role === "admin";
  const { cityName } = useSelector((state) => state.model);
  const { model } = useDetect();

  const { isDetecting, numBoxes } = useSelector((state) => state.detection);
  const number_of_containersRef = useRef(numBoxes);
  const isContainer = activeButton === "Container";

  const { devices, selectedDeviceId, setSelectedDeviceId, isPortrait } =
    useCam();
  const numDevices = devices.length;

  const [isCamActive, setIsCamActive] = useState(false);
  const [camMessage, setIsCamMessage] = useState("");
  const [secDelay, setSecDelay] = useState(2);
  const [shouldFlip, setShouldFlip] = useState(false);
  const shouldFlipRef = useRef(shouldFlip); // Ref to avoid stale closure

  const { startWatching, stopWatching } = useLocation();
  const { location } = useSelector((state) => state.detection);
  const locationRef = useRef(location);
  const city = useRef(cityName);
  const lastUploadTimeRef = useRef(0); // For upload throttling

  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const OrgcanvasRef = useRef(null);
  const intervalRef = useRef(null);

  // Keep shouldFlipRef in sync with state
  useEffect(() => {
    shouldFlipRef.current = shouldFlip;
  }, [shouldFlip]);

  const startDetection = () => {
    dispatch(setIsDetecting(true));
    processFrames();
  };

  const stopDetection = useCallback(() => {
    clearInterval(intervalRef.current);
    dispatch(setIsDetecting(false));
    dispatch(setDetectionImage(null));
    dispatch(setInferenceTime(0));
  }, [dispatch, intervalRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDetection();
    };
  }, [stopDetection]);

  // Start continuous GPS watching on mount, stop on unmount
  useEffect(() => {
    startWatching();
    return () => stopWatching();
  }, [startWatching, stopWatching]);

  // Keep location ref in sync with Redux state
  useEffect(() => {
    locationRef.current = location;
    city.current = cityName;
  }, [location, cityName]);

  useEffect(() => {
    number_of_containersRef.current = numBoxes;
  }, [numBoxes]);

  // Wake Lock: prevent screen from sleeping while detecting (mobile)
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch (err) {
        console.log("Wake Lock request failed:", err);
      }
    };

    if (isDetecting) {
      requestWakeLock();
    }

    return () => {
      if (wakeLock) {
        wakeLock.release();
        wakeLock = null;
      }
    };
  }, [isDetecting]);

  const sendImageToServer = async (imageDataUrl) => {
    const urlEndpoint = isContainer
      ? "/v1/detected-images"
      : "/v1/collected-images";

    if (
      locationRef.current.latitude === -1 ||
      locationRef.current.longitude === -1
    ) {
      toast.error("Location not available");
      return;
    }

    // Throttle uploads to avoid spamming the backend
    const now = Date.now();
    if (now - lastUploadTimeRef.current < MIN_UPLOAD_INTERVAL) return;
    lastUploadTimeRef.current = now;

    try {
      const formData = new FormData();
      const blob = await (await fetch(imageDataUrl)).blob();
      formData.append("image", blob, "image.png");
      formData.append("Latitude", locationRef.current.latitude);
      formData.append("Longitude", locationRef.current.longitude);
      formData.append("city", city.current);
      formData.append("number_of_containers", number_of_containersRef.current);

      const response = await fetch(API_URL + urlEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Unauthorized access. Please login again.");
          dispatch(setLogout());
          stopDetection();
          navigate("/login");
          return;
        }
        throw new Error("Failed to send image to server");
      }
      if (response.ok) {
        setImageCount((count) => count + 1);
      }
    } catch (error) {
      console.error("Error sending image to server:", error);
      toast.error("Error sending image to server");
      stopDetection();
    }
  };

  const processFrames = async () => {
    if (isContainer && !model) return;
    dispatch(setIsLoading(true));

    const video = webcamRef.current?.video;
    const OrgCanvas = OrgcanvasRef.current;
    const canvas = canvasRef.current;
    if (!video || !OrgcanvasRef) return;

    const cto = OrgCanvas.getContext("2d", { willReadFrequently: true });
    OrgCanvas.width = video.videoWidth;
    OrgCanvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = 640;
    canvas.height = 640;

    const processFrame = async () => {
      const startTime = performance.now();

      // Use ref to get current flip state (avoids stale closure)
      if (shouldFlipRef.current) {
        cto.save();
        cto.clearRect(0, 0, OrgCanvas.width, OrgCanvas.height);
        cto.translate(OrgCanvas.width, OrgCanvas.height);
        cto.scale(-1, -1);
        cto.drawImage(video, 0, 0, OrgCanvas.width, OrgCanvas.height);
        cto.restore();

        ctx.save();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.translate(canvas.width, canvas.height);
        ctx.scale(-1, -1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        cto.clearRect(0, 0, OrgCanvas.width, OrgCanvas.height);
        cto.drawImage(video, 0, 0, OrgCanvas.width, OrgCanvas.height);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      // Location is now continuously updated via watchPosition — no need to call fetchLocation()

      if (!isContainer) {
        dispatch(setIsLoading(false));
        await sendImageToServer(OrgCanvas.toDataURL());
        return;
      }

      const inputTensor = tf.tidy(() => {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let tensor = tf.browser.fromPixels(imageData);
        return tensor.expandDims(0).div(255.0);
      });

      const output = model.predict(inputTensor);
      const boxes = processTfjsYOLOOutput(output, 0.6);

      let yolo_classes = [];
      if (cityName === "Tarragona") {
        yolo_classes = ["Envasos", "Organic", "Paper", "Rebuig", "Vindre"];
      } else {
        yolo_classes = ["Rebuig", "Organic", "Vindre", "Envasos", "Paper"];
      }

      dispatch(setNumBoxes(boxes.length));
      if (boxes.length > 0 && !isDisplay) {
        await sendImageToServer(OrgCanvas.toDataURL());
      }
      if (boxes.length > 0 && isDisplay) {
        boxes.forEach((box) => {
          const text = `${yolo_classes[box.class]}: ${(
            box.confidence * 100
          ).toFixed(1)}%`;
          const textWidth = ctx.measureText(text).width;
          const padding = 4;
          const textHeight = 18;
          const [x1, y1, x2, y2] = box.bbox;
          ctx.strokeStyle = color_map[yolo_classes[box.class]];
          ctx.lineWidth = 2;
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

          ctx.fillStyle = color_map[yolo_classes[box.class]];
          ctx.fillRect(
            x1,
            y1 > 20 ? y1 - textHeight - padding : y1 + 20,
            textWidth + padding * 2,
            textHeight + padding
          );

          ctx.fillStyle =
            yolo_classes[box.class] === "Envasos" ? "black" : "white";
          ctx.font = "16px Arial";
          ctx.fillText(
            text,
            x1 + padding,
            y1 > 20 ? y1 - padding : y1 + 20 + textHeight
          );
        });
      }

      // Only dispose inputTensor — output is already disposed inside processTfjsYOLOOutput
      tf.dispose(inputTensor);
      isDisplay && dispatch(setDetectionImage(canvas.toDataURL()));
      dispatch(setIsLoading(false));
      const endTime = performance.now();
      dispatch(setInferenceTime((endTime - startTime).toFixed(2)));
    };

    intervalRef.current = setInterval(processFrame, secDelay * 1000);
  };

  const handleSwitchCamera = () => {
    const index = devices.findIndex(
      (device) => device.deviceId === selectedDeviceId
    );
    const nextIndex = (index + 1) % devices.length;
    setSelectedDeviceId(devices[nextIndex].deviceId);
  };
  const handleUserMedia = () => {
    setIsCamActive(true);
    setIsCamMessage("Webcam feed is active");
  };
  const handleUserMediaError = (error) => {
    setIsCamMessage(`🚫 Error accessing the camera 😢`);
    setIsCamActive(false);
  };

  return (
    <div className={style.imageUpload}>
      <h2>Inspection Mode</h2>
      {numDevices < 1 && (
        <p
          className={style.cameraError}
          style={{ fontSize: "20px", fontWeight: "700", color: "red" }}
        >
          🚫 No camera devices found 😢
        </p>
      )}
      {numDevices > 0 && (
        <p
          className={style.cameraError}
          style={{ fontSize: "20px", fontWeight: "700", color: "red" }}
        >
          {camMessage}
        </p>
      )}
      {isAdmin && !isDetecting && isCollecting && numDevices > 0 && (
        <SwitchButton
          activeButton={activeButton}
          setActiveButton={setActiveButton}
        />
      )}
      {isAdmin && !isDetecting ? (
        <MultiSelect secDelay={secDelay} setSecDelay={setSecDelay} />
      ) : (
        <>
          <p className={style.delay}>{`You are in ${
            isContainer ? "Detect" : "Collect"
          } Mode`}</p>
          <p className={style.delay}>🕓 Delay : {secDelay} sec</p>
        </>
      )}
      <div className="imageContainer">
        {isAdmin && (
          <button
            onClick={() => setShouldFlip((prevShouldFlip) => !prevShouldFlip)}
            className={`${style.flipbtn} ${shouldFlip ? style.flipactive : ""}`}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/flip_icon.png`}
              alt="flip camera"
            />
          </button>
        )}
        {numDevices > 1 && (
          <button onClick={handleSwitchCamera} className="cameraBtn">
            <img
              src={`${process.env.PUBLIC_URL}/images/switch-camera.png`}
              alt="change camera"
            />
          </button>
        )}
        {!(numDevices < 1) && (
          <>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <canvas ref={OrgcanvasRef} style={{ display: "none" }} />
            <Webcam
              ref={webcamRef}
              videoConstraints={{
                deviceId: selectedDeviceId,
                width: { ideal: isPortrait ? 1080 : 1440 },
                height: { ideal: isPortrait ? 1440 : 1080 },
              }}
              onUserMedia={handleUserMedia}
              onUserMediaError={handleUserMediaError}
            />
            {isCamActive ? (
              !isDetecting ? (
                <button className={style.detectBtn} onClick={startDetection}>
                  <span className={style.openCam}></span>
                </button>
              ) : (
                <button className={style.detectBtn} onClick={stopDetection}>
                  <span className={style.closeCam}></span>
                </button>
              )
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default VideoDetection;
